import { memo, useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Alert, Toast, Popover, Button } from 'reaxl';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import { sendImpressions, sendClick } from 'reaxl-analytics';
import { myWalletNonDealNotificationsImpression, seeDetailsGlobalNavDealImpression, seeDetailsGlobalNavDealClick, myWalletNonDealNotificationsClick } from 'reaxl-analytics-handlers';
import { WALLET_RESET_DEAL_NOTIFICATION_REQUEST, WALLET_UPDATE_DEAL_NOTIFICATION_REQUEST } from 'wallet-manager';
import { eventBus } from '@atc/cai-event-bus';
import { BrowserStorage } from '@atc/browser-storage';
import { jsx, jsxs } from 'react/jsx-runtime';
import getPopoverData from './helpers/getPopoverData.js';
import getLocationMessage from './helpers/getLocationMessage.js';
const walletNotifCache = /* #__PURE__*/new BrowserStorage('WALLET_NOTIF_POPOVER', {
  persist: true
});
function PopoverBody(_ref) {
  let {
    popoverData,
    onLinkClick,
    isToast = false
  } = _ref;
  return /*#__PURE__*/jsxs("div", {
    className: !isToast ? 'display-flex' : '',
    children: [/*#__PURE__*/jsx("div", {
      className: !isToast ? 'text-bold margin-right-1' : '',
      children: popoverData.message
    }), /*#__PURE__*/jsx(Button, {
      bsStyle: isToast ? 'text-decorated-inverse' : 'link',
      className: "padding-0 border-0",
      "data-panel": popoverData.panel,
      onClick: onLinkClick,
      children: popoverData.label || 'See Details'
    })]
  });
}
const WalletHeaderPopover = /*#__PURE__*/memo(_ref2 => {
  let {
    onClick,
    onCloseClick = () => {},
    hasNewTradeIn,
    dealNotifications,
    dealDetails = {},
    paymentsDuck,
    dealsDuck,
    uiContext = 'popover',
    position = 'top'
  } = _ref2;
  const dispatch = useDispatch();
  const [showWalletAlert, setShowWalletAlert] = useState(true);
  const sentImpression = useRef(false);
  const popoverData = getPopoverData(hasNewTradeIn, dealNotifications, dealDetails);
  useEffect(() => {
    const dismissalCache = walletNotifCache?.getItem();

    // Check the global 'show' flag used by other components
    if (dismissalCache?.show === false) {
      setShowWalletAlert(false);
    }
  }, [popoverData, dealNotifications]);
  useEffect(() => {
    const isNonDeal = popoverData?.dealReminders?.length > 0;
    const showPopover = uiContext !== 'alert' || !showWalletAlert;
    if (sentImpression.current || !showPopover || _isEmpty(popoverData)) {
      return;
    }
    sentImpression.current = true;
    if (isNonDeal) {
      sendImpressions({
        name: myWalletNonDealNotificationsImpression,
        data: {
          textContent: popoverData.label
        }
      });
    } else {
      sendImpressions({
        name: seeDetailsGlobalNavDealImpression,
        data: {
          location: getLocationMessage(hasNewTradeIn, dealNotifications)
        }
      });
    }
  }, [popoverData, uiContext, showWalletAlert, hasNewTradeIn, dealNotifications]);
  const updateDealReminders = () => {
    popoverData?.dealReminders?.forEach(_ref3 => {
      let {
        dealId,
        updateKey
      } = _ref3;
      eventBus.emit(WALLET_UPDATE_DEAL_NOTIFICATION_REQUEST, {
        dealId,
        body: {
          [updateKey]: true
        }
      });
      const action = dealsDuck?.creators?.updateDealReminders({
        dealId,
        [updateKey]: true
      });
      if (action) {
        try {
          dispatch(action);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error updating deal reminders', error);
        }
      }
    });
  };
  const handleNonDealClick = e => {
    const {
      deal
    } = popoverData;
    const nonDealData = {
      textContent: e.target.textContent,
      vehicle: dealDetails?.[deal.id]?.vehicle
    };
    updateDealReminders();
    sendClick(myWalletNonDealNotificationsClick, e, nonDealData);
  };
  const handlePopoverLinkClick = e => {
    if (popoverData?.dealReminders?.length > 0) {
      handleNonDealClick(e);
    } else {
      sendClick(seeDetailsGlobalNavDealClick, e, {
        location: getLocationMessage(hasNewTradeIn, dealNotifications)
      });
    }
    onClick(e);
    onCloseClick();
    setShowWalletAlert(false);
  };
  const handlePopoverCloseClick = () => {
    const isDealRelated = popoverData?.panel === 'activeDeals' || popoverData?.dealReminders?.length > 0;
    if (isDealRelated) {
      const dealIds = popoverData?.dealReminders?.map(_ref4 => {
        let {
          dealId
        } = _ref4;
        return dealId;
      }) || [];
      eventBus.emit(WALLET_RESET_DEAL_NOTIFICATION_REQUEST, {
        dealIds
      });
      dispatch(paymentsDuck?.creators?.resetDealNotifications());
      updateDealReminders();
    }
    onCloseClick();
    setShowWalletAlert(false);
  };
  if (_isEmpty(popoverData)) {
    return null;
  }
  if (uiContext === 'alert' && showWalletAlert) {
    return /*#__PURE__*/jsx(Alert, {
      bsStyle: "info",
      onDismiss: handlePopoverCloseClick,
      children: /*#__PURE__*/jsx(PopoverBody, {
        popoverData: popoverData,
        onLinkClick: handlePopoverLinkClick
      })
    });
  }
  if (uiContext === 'toast') {
    return /*#__PURE__*/jsx(Toast, {
      show: true,
      uiContext: "info",
      speed: "slow",
      position: position,
      heading: "My Wallet Alert!",
      onDismiss: handlePopoverCloseClick,
      body: /*#__PURE__*/jsx(PopoverBody, {
        popoverData: popoverData,
        onLinkClick: handlePopoverLinkClick,
        isToast: true
      })
    });
  }
  if (uiContext === 'popover') {
    return /*#__PURE__*/jsx(Popover, {
      id: "header-wallet-popover",
      className: "margin-top-3 header-wallet-popover",
      placement: "bottom",
      onCloseClick: handlePopoverCloseClick,
      children: /*#__PURE__*/jsx(PopoverBody, {
        popoverData: popoverData,
        onLinkClick: handlePopoverLinkClick
      })
    });
  }
  return null;
}, (prevProps, nextProps) => _isEqual(prevProps, nextProps));
export { WalletHeaderPopover as default };
